import React, { forwardRef, useCallback } from 'react'
import { GoalId, UserProfileDto, UserId, CompanyId, GoalWithProgress } from '../../services/api.types'
import {
  Chat,
  Channel,
  MessageInput,
  MessageList,
  Thread,
  Window,
  useChannelActionContext,
  ReactionsList,
  ReactionSelector,
  MessageInputProps,
} from 'stream-chat-react'

import './ChallengeChat.scss'

import { getFullName } from '../../common/helpers/translations'
import { CustomChannelHeader } from './components/CustomChannelHeader/CustomChannelHeader'
import ChatSnackbar from './components/ChatSnackbar/ChatSnackbar'
import Loader from '../../components/Loader/Loader'
import { useInitChat } from './hooks/useInitChat'
import i18nInstance from './i18n/i18nInstance'
import { getCurrentCompanyGoal } from '../Activity/store/ActivityActions'
import { store } from '../../index'
import { push } from 'connected-react-router'
import { EmptyStateIndicator } from './components/EmptyStateIndicator'
import { CustomModalGallery } from './components/CustomModalGallery'
import { YumCustomMessageActionsList } from '../../components/YumChatView/YumMessageActions'
import { YumSystemMessage } from '../../components/YumChatView/YumSystemMessage'
import type { LocalMessage, Message, SendMessageOptions } from 'stream-chat'

export interface IChallengeChatProps {
  goalId: GoalId
  userDto: UserProfileDto
  goal?: GoalWithProgress
}

const ChannelInner = ({ userId, goalId, companyId }: { userId: UserId; goalId: GoalId; companyId?: CompanyId }) => {
  const { sendMessage } = useChannelActionContext()

  const overrideSubmitHandler: MessageInputProps['overrideSubmitHandler'] = useCallback(
    async ({
      localMessage,
      message,
      sendOptions,
    }: {
      cid: string
      localMessage: LocalMessage
      message: Message
      sendOptions: SendMessageOptions
    }) => {
      // Focus on input to keep showing the keyboard
      const el = document.getElementsByClassName('str-chat__textarea__textarea')
      if (el && el[0]) {
        // @ts-ignore
        el[0].focus()
      }

      // Send the message
      await sendMessage({ localMessage, message, options: sendOptions })
    },
    [sendMessage],
  )

  return (
    <>
      <Window>
        <CustomChannelHeader userId={userId} goalId={goalId} companyId={companyId} />
        <MessageList disableQuotedMessages hideDeletedMessages closeReactionSelectorOnClick onlySenderCanEdit />
        <MessageInput overrideSubmitHandler={overrideSubmitHandler} />
      </Window>
      <Thread />
    </>
  )
}

export default function ChallengeChat(props: IChallengeChatProps) {
  const { userDto, goalId, goal }: { userDto: UserProfileDto; goalId: GoalId; goal?: GoalWithProgress } = props
  const { userId, firstName, lastName, email } = userDto
  const name = getFullName(firstName, lastName, email)
  const participating = goal?.participating
  const { chatClient, currentChannel } = useInitChat(userId, name, goalId, participating)

  // Load goal(s) if it does not exist (coming from push notification).
  React.useEffect(() => {
    if (!goal) {
      store.dispatch(getCurrentCompanyGoal())
      // getCurrentCompanyGoal({ id: goalId })
    } else {
      if (!goal.chatEnabled) {
        store.dispatch(push(`/challenges/${goalId}/ranking`))
      }
    }
  }, [goal])

  if (!chatClient) return <Loader top={true} />

  //const CustomAvatar = () => <Initials fullName={name} size="sm" />

  /*const CustomImage: React.FC<ImageProps> = (props) => {
    /!*return <Attachment {...props} Gallery={CustomGallery} />*!/
    return (
      <ImageComponent
        {...props}
        thumb_url={
          'https://github.com/GetStream/stream-chat-react/blob/master/src/components/Attachment/Attachment.tsx'
        }
      />
    )
  }*/

  const customReactionOptions = [
    //{ type: 'haha', Component: () => <StreamEmoji fallback="😂" type="haha" />, name: 'Joy' },
    //{ type: 'like', Component: () => <StreamEmoji fallback="👍" type="like" />, name: 'Thumbs up' },
    //{ type: 'love', Component: () => <StreamEmoji fallback="❤️" type="love" />, name: 'Heart' },
    //{ type: 'sad', Component: () => <StreamEmoji fallback="😔" type="sad" />, name: 'Sad' },
    //{ type: 'wow', Component: () => <StreamEmoji fallback="😲" type="wow" />, name: 'Astonished' },
    {
      Component: () => <>😄</>,
      type: 'happy',
      name: 'Happy',
    },
    {
      Component: () => <>😂</>,
      type: 'haha',
      name: 'Joy',
    },
    {
      Component: () => <>👍</>,
      type: 'like',
      name: 'Thumbs up',
    },
    {
      Component: () => <>❤️</>,
      type: 'love',
      name: 'Heart',
    },
    {
      Component: () => <>😔</>,
      type: 'sad',
      name: 'Sad',
    },
    {
      Component: () => <>😲</>,
      type: 'wow',
      name: 'Astonished',
    },
    {
      Component: () => <>💪</>,
      type: 'flex',
      name: 'Flexed biceps',
    },
  ]

  const allCustomReactionOptions = [
    ...customReactionOptions,
    { type: 'angry', Component: () => <>😠</>, name: 'Angry' },
  ]

  // ReactionSelector component requires forwarded reference
  const CustomReactionSelector = forwardRef((props, ref) => (
    /* @ts-ignore */
    <ReactionSelector {...props} ref={ref} reactionOptions={customReactionOptions} />
  ))

  const CustomReactionsList = (props: any) => <ReactionsList {...props} reactionOptions={allCustomReactionOptions} />

  return (
    <>
      <Chat client={chatClient} i18nInstance={i18nInstance}>
        {/*<Channel channel={currentChannel} Avatar={CustomAvatar} acceptedFiles={['image/*']}>*/}
        {/*<Channel Attachment={CustomAttachment} channel={currentChannel} acceptedFiles={['image/*']}>*/}
        <Channel
          channel={currentChannel}
          reactionOptions={customReactionOptions}
          /* @ts-ignore */
          ReactionSelector={CustomReactionSelector}
          ReactionsList={CustomReactionsList}
          EmptyStateIndicator={EmptyStateIndicator}
          ModalGallery={CustomModalGallery}
          CustomMessageActionsList={YumCustomMessageActionsList}
          MessageSystem={YumSystemMessage}
        >
          <ChannelInner userId={userId} goalId={goalId} companyId={goal?.companyId} />
        </Channel>
      </Chat>
      <ChatSnackbar />
      {/*<Chat client={chatClient} i18nInstance={i18nInstance}>
        <Channel channel={currentChannel}>
          <Window>
            <ChannelHeader />
            <MessageList />
            <MessageInput />
          </Window>
          <Thread />
        </Channel>
      </Chat>*/}
    </>
  )
}
